import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import Sidebar from "./Sidebar";
import UploadTray from "@/components/UploadTray";
import ExistsMismatchReviewHost from "@/components/ExistsMismatchReviewHost";
import { Menu } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { PageHeaderProvider, PageHeaderTargetLeading, PageHeaderTarget, PageHeaderTargetRight, PageHeaderTitle, usePageHeader } from "@/contexts/PageHeaderContext";
import { UPLOAD_EXTRACT_TOAST_ID } from "@/contexts/UploadProgressContext";

const pageTitles = {
  "/": "Dashboard",
  "/weekly-dashboard": "Weekly Dashboard",
  "/billing": "Billing Dashboard",
  "/patients": "Patients",
  "/organizations": "Companies",
  "/hospitals": "Companies",
  "/studies": "Studies",
  "/studies/pending-deletions": "Pending Deletions",
  "/upload": "Upload",
  "/viewer": "Image Viewer",
  "/sharing": "Sharing",
  "/audit": "Audit Trail",
  "/users": "User Management",
  "/dashboard-cards": "Dashboard Cards",
  "/search": "Search & Discovery",
  "/reports": "Clinical Reports",
  "/flowchart": "Platform Flowchart",
};

const pageDescriptions = {

  "/": "Overview of studies, uploads, and activity across your network.",
  "/organizations": "Manage companies and the hospitals under them.",
  "/upload": "Upload and assign imaging studies to patients.",
  "/reports": "Review and manage clinical reports.",
  "/sharing": "Share studies and patients securely with collaborators.",
  "/search": "Find patients and studies across your network.",
  "/studies/pending-deletions": "Review and approve or reject patient and study deletion requests.",
  "/dashboard-cards": "Choose which stat cards appear on the dashboard for admins and users.",
};

const ShellHeading = ({ title, description }) => {
  const header = usePageHeader();
  const resolvedTitle = header?.titleOverride || title;
  const resolvedDescription = header?.titleOverride ? header?.descriptionOverride : description;

  return (
    <div className="min-w-0 flex-1">
      <PageHeaderTitle className="text-2xl sm:text-[28px] font-bold tracking-tight text-[var(--xl-text)] truncate">
        {resolvedTitle}
      </PageHeaderTitle>
      {resolvedDescription ? (
        <p className="text-sm text-[var(--xl-mut)] mt-1 max-w-xl hidden sm:block">
          {resolvedDescription}
        </p>
      ) : null}
    </div>
  );
};

export default function AppShell({ children }) {
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => (
    typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true
  ));

  useEffect(() => {
    const media = window.matchMedia("(min-width: 1024px)");
    const handleChange = () => setIsDesktop(media.matches);
    handleChange();
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!location.pathname.startsWith("/upload")) {
      toast.dismiss(UPLOAD_EXTRACT_TOAST_ID);
    }
  }, [location.pathname]);

  const pathKey = Object.keys(pageTitles)
    .filter((k) => (k === "/" ? location.pathname === "/" : location.pathname.startsWith(k)))
    .sort((a, b) => b.length - a.length)[0];
  const title = pageTitles[pathKey] || "XLSync";
  const description = pageDescriptions[pathKey];
  const isViewerPage = location.pathname.startsWith("/viewer");

  return (
    <PageHeaderProvider>
    <div className="flex h-screen overflow-hidden bg-[var(--xl-bg)]" data-testid="app-layout">
      <div className="hidden lg:block flex-shrink-0">
        <Sidebar />
      </div>

      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="left"
          className="p-0 w-[min(280px,85vw)] sm:max-w-[280px] border-r border-[var(--xl-sidebar-line)] bg-[var(--xl-sidebar)] text-[var(--xl-sidebar-text)] [&>button]:text-[var(--xl-sidebar-mut)] [&>button]:hover:text-[var(--xl-sidebar-text)]"
          data-testid="mobile-sidebar"
        >
          <Sidebar mobile onNavigate={() => setMobileNavOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {isViewerPage ? (
          <header
            className="lg:hidden flex-shrink-0 flex items-center px-4 pt-2 pb-1"
            data-testid="app-header"
          >
            <button
              type="button"
              className="w-10 h-10 flex-shrink-0 rounded-full border border-[var(--xl-line2)] bg-white grid place-items-center text-[var(--xl-mut)] hover:text-[var(--xl-text)] transition-colors"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open navigation menu"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
          </header>
        ) : (
          <header
            className="flex-shrink-0 flex flex-col gap-2 sm:gap-2.5 px-4 sm:px-6 lg:px-8 pt-5 sm:pt-6 pb-1 sm:pb-1.5"
            data-testid="app-header"
          >
            <div className="flex items-start sm:items-center justify-between gap-3 sm:gap-4">
              <div className="flex items-start gap-2 sm:gap-3 min-w-0 flex-1">
                <button
                  type="button"
                  className="lg:hidden w-10 h-10 flex-shrink-0 rounded-full border border-[var(--xl-line2)] bg-white grid place-items-center text-[var(--xl-mut)] hover:text-[var(--xl-text)] transition-colors mt-0.5"
                  onClick={() => setMobileNavOpen(true)}
                  aria-label="Open navigation menu"
                  data-testid="mobile-menu-btn"
                >
                  <Menu className="w-5 h-5" />
                </button>
                <PageHeaderTargetLeading className="empty:hidden flex items-center flex-shrink-0 mt-0.5" />
                <ShellHeading title={title} description={description} />
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                <PageHeaderTargetRight className="flex items-center" />
              </div>
            </div>
            <PageHeaderTarget className="empty:hidden flex flex-wrap items-center gap-2 sm:gap-3 min-w-0 w-full" />
          </header>
        )}

        <main
          className={`flex-1 min-h-0 flex flex-col px-4 sm:px-6 lg:px-8 ${
            isViewerPage
              ? "overflow-hidden pb-2 sm:pb-3 pt-2 sm:pt-3"
              : "overflow-auto pb-4 sm:pb-6"
          }`}
          data-testid="main-content"
        >
          {children}
        </main>
      </div>

      {!isDesktop ? <UploadTray /> : null}
      <ExistsMismatchReviewHost />
    </div>
    </PageHeaderProvider>
  );
}
